import { date } from 'quasar'
import i18next from 'i18next'

const { formatDate, extractDate } = date

/**
 * Parse string date to an instance of Date.
 *
 * @param {String} date - Date string in 'YYYY-MM-DDTHH:mm:ss.SSSSSSZ' format.
 * @returns {Date} - Instance of Date.
 */
export const getDateFromString = date => extractDate(date, 'YYYY-MM-DDTHH:mm:ss.SSSSSSZ')

/**
 * Parse instance of Date to a string.
 *
 * @param {String} date - Instance of Date.
 * @returns {Date} - Date string in 'YYYY-MM-DDTHH:mm:ss.SSSSSSZ' format.
 */
export const getStringFromDate = date => formatDate(date, 'YYYY-MM-DDTHH:mm:ss.SSSSSSZ')

/**
 * Get numeric Unix time from string timestamp.
 *
 * @param {String} date - Date string in 'YYYY-MM-DD' format.
 * @returns {Number} - Unix time.
 */
export const getUnixTime = date => date.getTime()

/**
 * Format date string from Unix time.
 *
 * @param {Number} date - Unix time.
 * @returns {String} - Stringified date in 'YYYY-MM-DD' format.
 */
export const formatDateFromTimeStamp = date => formatDate(date, 'YYYY-MM-DD')

/**
 * Find closest date from the array of given dates.
 *
 * @typedef {Object} ClosestDate
 * @property {Number} closestDate - Closest Date.
 * @property {Number} index - Index of the closest date from the dates stack.
 *
 * @param {Number} date - Date needle.
 * @param {Array.<{Date}>} dates - Array of Date objects (stack).
 * @returns {ClosestDate} - Closest date.
 */
export const findClosestDate = (date, dates) => {
  const closestDate = dates.reduce((prev, curr) => {
    return (Math.abs(curr - date) < Math.abs(prev - date) ? curr : prev)
  })
  const index = dates.indexOf(closestDate)
  return { closestDate, index }
}

/**
 * Returns a random Date instance between two given Date instances.
 *
 * @param {Date} startDate - The start Date instance.
 * @param {Date} endDate - The end Date instance.
 * @returns {Date} A random Date instance between the startDate and endDate.
 * @throws {Error} If startDate is not an instance of Date.
 * @throws {Error} If endDate is not an instance of Date.
 * @throws {Error} If startDate is after endDate.
 */
export const getRandomTimestamp = (startDate, endDate) => {
  // Check if the arguments are valid Date instances
  if (!(startDate instanceof Date)) {
    throw new Error('startDate must be a Date instance.')
  }
  if (!(endDate instanceof Date)) {
    throw new Error('endDate must be a Date instance.')
  }
  // Check if startDate is before endDate
  if (startDate > endDate) {
    throw new Error('startDate must be before endDate.')
  }

  // Get the timestamps in milliseconds
  const startTimestamp = startDate.getTime()
  const endTimestamp = endDate.getTime()

  // Generate a random timestamp between the start and end timestamps
  const randomTimestamp = Math.random() * (endTimestamp - startTimestamp) + startTimestamp

  // Return a new Date instance with the random timestamp
  return new Date(randomTimestamp)
}

/**
 * Returns a new Date instance with the specified number of milliseconds added to the provided date.
 *
 * @param {Date} date - The original Date instance.
 * @param {number} time - The number of milliseconds to add.
 * @returns {Date} A new Date instance with the added milliseconds.
 * @throws {Error} If date is not an instance of Date.
 * @throws {Error} If milliseconds is not a number.
 */
export const addTimeToDate = (date, time) => {
  // Check if the argument is a valid Date instance
  if (!(date instanceof Date)) {
    throw new Error('date must be a Date instance.')
  }
  // Check if milliseconds is a number
  if (typeof time !== 'number') {
    throw new Error('milliseconds must be a number.')
  }

  // Get the timestamp of the provided date in milliseconds
  const timestamp = date.getTime()

  // Add the specified milliseconds to the timestamp
  const newTimestamp = timestamp + time

  // Return a new Date instance with the new timestamp
  return new Date(newTimestamp)
}

/**
 * Get difference in datetime.
 *
 * @param {Date} min - Date to subtract from.
 * @param {Date} sub - Date to subtract.
 * @returns {Date} The difference in time.
 * @throws {Error} If date is not an instance of Date.
 * @throws {Error} If milliseconds is not a number.
 */
export const getDatetimeDifference = (min, sub) => {
  // Check if the argument is a valid Date instance
  if (!(min instanceof Date) || !(sub instanceof Date)) {
    throw new Error('min and sub must be a Date instance.')
  }
  const diffTime = min - sub
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) // eslint-disable-line

  return new Date(diffTime)
}

/**
 * Calculates the difference between two dates and returns an object with the value and range.
 * The range is automatically determined to be the most significant time range (years, months, etc.).
 * Both positive and negative differences are supported.
 *
 * @param {Date} min - The minuend (date to subtract from)
 * @param {Date} sub - The subtrahend (date to subtract)
 * @param {'long'|'short'|'narrow'} [style='long'] - Intl.RelativeTimeFormat style for `human`.
 * @returns {Object} An object with three properties:
 *                   - val: {number} The numeric difference
 *                   - range: {string} The most significant time range ('years', 'months', etc.)
 *                   - human: {string} The formatted relative-time string
 * @throws {Error} If date is not an instance of Date.
 */
export const getDatetimeDiff = (min, sub, style = 'long') => {
  // Check if the argument is a valid Date instance
  if (!(min instanceof Date) || !(sub instanceof Date)) {
    throw new Error('min and sub must be a Date instance.')
  }

  // Calculate raw difference in milliseconds
  const diffMs = min.getTime() - sub.getTime()

  // Calculate absolute difference in various ranges
  const diffSeconds = Math.abs(diffMs) / 1000
  const diffMinutes = diffSeconds / 60
  const diffHours = diffMinutes / 60
  const diffDays = diffHours / 24
  const diffWeeks = diffDays / 7

  // Approximate months and years (not precise but good for relative display)
  const diffMonths = diffDays / 30.44 // Average days in month
  const diffYears = diffDays / 365.25 // Account for leap years

  // Determine the most significant range
  let val, range

  if (Math.abs(diffYears) >= 1) {
    val = diffMs / (1000 * 60 * 60 * 24 * 365.25)
    range = 'years'
  } else if (Math.abs(diffMonths) >= 1) {
    val = diffMs / (1000 * 60 * 60 * 24 * 30.44)
    range = 'months'
  } else if (Math.abs(diffWeeks) >= 1) { // NEW: Added weeks check
    val = diffMs / (1000 * 60 * 60 * 24 * 7)
    range = 'weeks'
  } else if (Math.abs(diffDays) >= 1) {
    val = diffMs / (1000 * 60 * 60 * 24)
    range = 'days'
  } else if (Math.abs(diffHours) >= 1) {
    val = diffMs / (1000 * 60 * 60)
    range = 'hours'
  } else if (Math.abs(diffMinutes) >= 1) {
    val = diffMs / (1000 * 60)
    range = 'minutes'
  } else {
    val = diffMs / 1000
    range = 'seconds'
  }

  // Round to 1 decimal place.
  // val = Math.round(val * 10) / 10

  // Round to nearest integer.
  val = Math.round(val)

  // Convert to human readable relative time.
  const human = i18next.t('datetime:intlDateTime', { val, range, style })

  return { val, range, human }

  // Example usage:
  // const result = getDateDifference(new Date(), new Date('2023-01-01'))
  // console.log(result) // { val: 1.2, range: 'years' }
}

/**
 * Convenience wrapper around getDatetimeDiff for the common "how long ago was
 * this" case — a short, locale-aware relative string (e.g. "5d ago") for a
 * single date, via i18next's built-in `relativetime` formatter (which wraps
 * Intl.RelativeTimeFormat under the hood, no extra date library needed).
 *
 * @param {Date|string} dateInput - The date to describe (Date instance or ISO string).
 * @param {Date} [now] - What "now" is; pass an explicit, ticking value from a
 *   reactive caller (see useRelativeNow) to keep the string up to date — a plain
 *   call without it is a one-off snapshot.
 * @returns {string|null} e.g. "5d ago", or null if dateInput is falsy.
 */
export const formatRelativeDate = (dateInput, now = new Date()) => {
  if (!dateInput) return null
  const target = dateInput instanceof Date ? dateInput : new Date(dateInput)
  return getDatetimeDiff(target, now, 'narrow').human
}
